import React, { useState, useLayoutEffect, useEffect, useRef, useMemo } from "react";
import { Input, Select } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";

const normalizeTagsValue = (value) => {
    if (Array.isArray(value)) {
        return value.map((tag) => String(tag).trim()).filter(Boolean);
    }
    if (!value) return [];
    return String(value)
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
};

const formatTagsForSave = (tags) =>
    normalizeTagsValue(tags).join(",");

const EditableCell = ({
    record,
    field,
    value,
    isSpApiConnected,
    isEditing,
    onStartEdit,
    onSave,
    onCancel,
    tagOptions = [],
    tagsLoading = false,
}) => {
    const [draft, setDraft] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const inputRef = useRef(null);
    const editContainerRef = useRef(null);
    const wasEditingRef = useRef(false);

    const isTagsField = field === "tags";
    const isNumberField = !isTagsField;

    const tagSelectOptions = useMemo(
        () => tagOptions.map((tag) => ({ label: tag, value: tag })),
        [tagOptions]
    );

    useLayoutEffect(() => {
        if (isEditing && !wasEditingRef.current) {
            setDraft(isTagsField ? formatTagsForSave(value) : (value ?? ""));
            setDropdownOpen(isTagsField);
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        }
        if (!isEditing) {
            setDropdownOpen(false);
        }
        wasEditingRef.current = isEditing;
    }, [isEditing, value, field, isTagsField]);

    useEffect(() => {
        if (!isEditing) return undefined;

        const handleClickOutside = (event) => {
            if (editContainerRef.current?.contains(event.target)) return;
            if (event.target.closest(".ant-select-dropdown")) return;
            onCancel();
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isEditing, onCancel]);

    const showTagsLoading = isTagsField && tagsLoading && tagOptions.length === 0;
    const tagSelectValue = normalizeTagsValue(isTagsField ? draft : "");
    const displayValue = isTagsField
        ? (normalizeTagsValue(value).join(", ") || "-")
        : (value ?? "-");

    const handleStartEdit = (e) => {
        if (e) e.stopPropagation();
        if (!isSpApiConnected) return;
        onStartEdit(record, field);
    };

    const handleSave = (e) => {
        if (e) e.stopPropagation();
        onSave(record, field, draft);
    };

    const handleCancel = (e) => {
        if (e) e.stopPropagation();
        onCancel();
    };

    if (!isEditing) {
        return (
            <div
                className="editable-cell-container"
                onClick={handleStartEdit}
                style={{
                    cursor: isSpApiConnected ? "pointer" : "default",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 8px",
                    borderRadius: "4px",
                }}
            >
                <EditOutlined className="products-edit-icon" style={{ fontSize: "12px" }} />
                <span className="products-edit-value">{displayValue}</span>
            </div>
        );
    }

    return (
        <div
            ref={editContainerRef}
            style={{ display: "flex", gap: "6px", alignItems: "center" }}
            onClick={(e) => e.stopPropagation()}
        >
            {isTagsField ? (
                <Select
                    ref={inputRef}
                    mode="tags"
                    size="small"
                    autoFocus
                    open={dropdownOpen}
                    onOpenChange={setDropdownOpen}
                    value={tagSelectValue}
                    onChange={(tags) => setDraft(formatTagsForSave(tags))}
                    options={tagSelectOptions}
                    loading={showTagsLoading}
                    placeholder={showTagsLoading ? "Loading tags..." : "Select or type tags"}
                    style={{ minWidth: "180px", maxWidth: "240px" }}
                    tokenSeparators={[","]}
                    notFoundContent={showTagsLoading ? null : "Type to add a new tag"}
                />
            ) : (
                <Input
                    ref={inputRef}
                    size="small"
                    type={isNumberField ? "number" : "text"}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    style={{ width: "120px" }}
                    onPressEnter={handleSave}
                />
            )}
            <CheckOutlined
                onClick={handleSave}
                className="products-edit-confirm"
                style={{ cursor: "pointer" }}
            />
            <CloseOutlined
                onClick={handleCancel}
                className="products-edit-cancel"
                style={{ cursor: "pointer" }}
            />
        </div>
    );
};

export default EditableCell;
